<script setup lang="ts">
/**
 * Singapura — Active-Exempt-unaudited AGM 5종(CSI003). Studio panel: 5문서 패키지 + create.
 *   cdd/CddVarField/useCddFill 는 aura/agm 재사용. 서명 [[Signers]] 는 문서별 signers 로 백엔드가 렌더:
 *     resln·notice = 이사, agreement·attendance = 주주, minutes = [[chairman_name]] 변수(서명자 없음).
 *   자동채움: 회사·UEN·FYE·등록주소 + 이사(ds_signatories/chairman/retiring) + 주주(paid_up_shares) + 감사.
 */
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, typeOf } from '../../aura/agm/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from '../../aura/agm/cdd/agmSignersContext';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import AgmPackageDoc from '../agm/AgmPackageDoc.vue';
import { normalizeSingapuraAgmForWord } from '../utils/normalizeSingapuraAgmForWord';
import { toLongDate } from '../utils/longDate';
import { formatAppliedAddress } from '../utils/formatAppliedAddress';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: { key: string; line?: string; line1?: string; line2?: string; line3?: string; town?: string; state?: string; postcode?: string; country?: string; seq?: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideAgmFill({ formatDate: toLongDate }); // 화면 날짜도 "30 June 2026" (고객 요청 2026-10-15)
const signersCtx = provideAgmSigners(); // 문서 [[Signers]] = AgmSigners 위젯(공용 SignersField)
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
const taskConfigSteps = ref<string[]>([]);
const TASK_CODE = 'CSI003';
let signerSeq = 0;
function mkSigner(name: string, title: string, role = 'director'): SignerEntry {
  return { id: `agm-si-${signerSeq++}`, name: String(name || '').trim(), title, role, isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide(AGM_ENTITY_CD, entityCd); // AgmSigners 의 직책별 서명자 선택 다이얼로그 활성화

const taskTitleLocal = ref('');
const titleModel = computed({ get: () => taskTitleLocal.value || props.taskTitle || '', set: (v: string) => { taskTitleLocal.value = v; } });
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

const st = (v: unknown) => String(v ?? '').trim();
function isoDate(v: unknown): string { const s = st(v); const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/); return m ? `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` : s; }
function joinNames(names: string[]): string {
  const n = names.filter(Boolean);
  if (n.length <= 1) return n[0] || '';
  if (n.length === 2) return `${n[0]} and ${n[1]}`;
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

// person lookup (chairman/retiring director 등)
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    emit('request-person-lookup', { fieldGroup: 'singapura-agm', key: bareKey(key), scopedKey: key, defaultRole: 'DIRECTOR', lookupType: 'officer', entityCd: entityCd.value });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });
watch(() => props.selectedPerson, (p) => { if (p && fill.activeKey.value) { fill.applyPersonToActive(p as Record<string, any>); emitProgress(); } });
// 📍 주소 입력 결과 → 그 칸(Meeting venue 등)에 반영. 스튜디오는 key 가 붙은 appliedAddress 로 돌아온다.
//   (선언만 있고 반영 처리가 없어 챗봇에서 입력한 주소가 안 들어가던 문제 — Registers 패널과 같은 처리)
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value); emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = formatAppliedAddress(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

/** Edit 재진입 — 저장값(restoreState)을 복원했으면 엔티티 자동채움이 덮어쓰지 않는다(빈 칸만 채움). */
const restoredFromSaved = ref(false);

/** 이름 기준 중복 제거 — 엔티티 임원 목록은 bizfile 동기화(auto)와 수동(manual)에 같은 사람이 함께 있을 수 있다. */
function uniqNames(list: string[]): string[] {
  const seen = new Set<string>();
  return list.filter((n) => { const k = n.trim().toLowerCase(); if (!k || seen.has(k)) return false; seen.add(k); return true; });
}

// 자동채움 — 회사·FYE·등록주소 + 이사 + 주주 + 감사
async function autofillFromEntity(cdRaw: string) {
  const cd = st(cdRaw);
  if (!cd || cd === lastAutofillCd.value) return;
  // 다른 회사로 바꾼 경우는 저장값 보존이 아니라 새 회사 값으로 채운다
  if (lastAutofillCd.value && lastAutofillCd.value !== cd) restoredFromSaved.value = false;
  lastAutofillCd.value = cd;
  // ⚠ Edit 재진입: 저장된 선택값(재선임 옵션·서명자 등)을 지우거나 덮어쓰지 않는다 — 예전엔 기본값으로 돌아갔다.
  if (!restoredFromSaved.value) {
    for (const k of autofilledKeys.value) fill.setValue(k, '');
    signersCtx.clearAll(); // 회사 변경 시 서명자 재시드
  }
  const written = new Set<string>();
  const set = (k: string, v: unknown) => {
    const s = st(v);
    if (!s) return;
    if (restoredFromSaved.value && st(fill.values[k])) return; // 저장값 보존
    fill.setValue(k, s); written.add(k);
  };
  let members: any[] = [];
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_uen', src.registrationNo);
    set('financial_year_end', isoDate(src.financialYearEnd));
    if (src.registeredOfficeAddress) { set('registered_office_address', src.registeredOfficeAddress); set('meeting_venue', src.registeredOfficeAddress); }
    members = Array.isArray(src.members) ? src.members : [];
    // 납입 주식수 = 주주 보유 합계(요약).
    const totalShares = members.reduce((s: number, m: any) => s + (Number(m?.sharesTotal) || 0), 0);
    if (totalShares > 0) set('paid_up_shares', totalShares.toLocaleString());
  } catch (e) { console.warn('[Singapura-AGM] agm-source failed', e); }

  const officersOf = async (group: 'director' | 'secretary' | 'auditor'): Promise<any[]> => {
    try {
      const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: group });
      const off: any = res?.data?.data ?? res?.data ?? res ?? {};
      return [...(off.autoOfficers || []), ...(off.manualOfficers || [])].filter((o) => st(o?.cessationDate) === '' && st(o?.status) !== 'Cessation');
    } catch { return []; }
  };

  // 이사 — 서명자(agm_Signers) 시드(수동 편집 없을 때만).
  const dirs = uniqNames((await officersOf('director')).map((o) => st(o.fullName || o.contactName)).filter(Boolean));
  if (dirs.length) {
    set('ds_authorised_directors', joinNames(dirs));
    set('chairman_name', dirs[0]);
    set('retiring_director_name', dirs[0]);
    if (dirs.length > 1) set('retiring_directors', joinNames(dirs));
    set('director_rotation', dirs.length <= 1 ? 'none' : 'single');
    set('director_count', String(dirs.length));
    if (!signersCtx.signersOf('agm_Signers').length) signersCtx.setSigners('agm_Signers', dirs.map((n) => mkSigner(n, 'Director', 'director')));
  }

  // 주주 — 서명자(agm_member_Signers) 시드(수동 편집 없을 때만).
  const named = uniqNames(members.filter((m: any) => st(m?.name)).map((m: any) => st(m.name)));
  if (named.length) {
    set('member_count', String(named.length)); set('members_scope', named.length === 1 ? 'sole' : 'all');
    if (!signersCtx.signersOf('agm_member_Signers').length) signersCtx.setSigners('agm_member_Signers', named.map((n) => mkSigner(n, 'Member', 'shareholder')));
  }

  // 감사인(있으면)
  const auds = uniqNames((await officersOf('auditor')).map((o) => st(o.fullName || o.contactName)).filter(Boolean));
  if (auds.length) set('auditor_name', auds[0]);

  autofilledKeys.value = written;
  emitProgress();
}
watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = st(fd.company_name || fd.entityName);
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = st(fd.entityCd || fd.icpCd);
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

onMounted(async () => {
  emitProgress();
  try { const cfg: any = await getTaskConfigByCode(st(props.selection.task.TASK_CODE) || TASK_CODE); taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : []; } catch { /* ignore */ }
});

function gatherState() { return { values: { ...fill.exportPayload() }, entityCd: entityCd.value, taskTitle: effectiveTitle.value, signers: signersCtx.exportAll() }; }
function restoreState(s: any) {
  restoredFromSaved.value = true;
  if (s?.values) for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  if (s?.signers) signersCtx.importAll(s.signers);
}
function companyFormData() { return normalizeSingapuraAgmForWord(fill.exportPayload()); }

// 문서 분류(파일명/코드) → 서명자 그룹.
function classifyDoc(name: string): 'resln' | 'notice' | 'agreement' | 'minutes' | 'attendance' | 'other' {
  const s = name.toLowerCase().replace(/[_\-.]+/g, ' ');
  if (/resln|resolution/.test(s)) return 'resln';
  // ⚠ 1.c 파일명 "AGM-agreemt to shorter notice" 에도 'notice' 가 들어 있다 → agreement 를 notice 보다 먼저 판정
  //   (순서가 바뀌면 1.c 가 Notice 로 분류돼 이사 서명자·Notice 정렬 옵션이 들어간다)
  if (/agreem|shorter/.test(s)) return 'agreement';
  if (/notice/.test(s)) return 'notice';
  if (/\bmin\b|minute/.test(s)) return 'minutes';
  if (/att|attend|present/.test(s)) return 'attendance';
  return 'other';
}
function signersFor(kind: string): SignerEntry[] | undefined {
  if (kind === 'resln' || kind === 'notice') { const s = signersCtx.signersOf('agm_Signers'); return s.length ? s : undefined; }
  if (kind === 'agreement' || kind === 'attendance') { const s = signersCtx.signersOf('agm_member_Signers'); return s.length ? s : undefined; }
  return undefined; // minutes: [[chairman_name]] 변수 사용, 서명자 없음
}

/**
 * 문서별 서명 블록 배치(백엔드 replaceSignerBlocks 가 formData 에서 읽음, 고객 요청 2026-10-15):
 *   1.a Resolution(이사) · 1.c Agreement(주주) · 1.e Attendance(주주) — 서명자 1명이면 가운데
 *   1.b Notice — 서명줄 아래 이름·직책 왼쪽 정렬
 *   ⚠ 키는 서명 단어(signer/signature…)로 끝나면 안 됨(UI_SIGNER_PICKER_RE 오인) → signblock_* 사용.
 */
function signBlockLayout(kind: string): Record<string, string> {
  if (kind === 'resln' || kind === 'agreement' || kind === 'attendance') return { signblock_single_center: 'yes' };
  if (kind === 'notice') return { signblock_name_align: 'left' };
  return {};
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = st(props.selection.task.TASK_CODE) || TASK_CODE;
    const companyBase = companyFormData();
    const officerData = { entityCd: entityCd.value || null, icpCd: entityCd.value || null, presetForm: gatherState(), docType: 'SG_AR_AGM_EXEMPT', isMyPreset: props.selection?.isMyPreset === true, formData: companyBase };
    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskData: { formData: officerData } }) });
      await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskTitle: effectiveTitle.value }) });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null, taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Active-Exempt-unaudited AGM',
          taskTitle: effectiveTitle.value,
          taskFlow: (() => { const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase()); return { createDocument: steps.length === 0 ? true : steps.includes('doc'), requestTSign: steps.includes('t-sign'), proceedWithEFiling: steps.includes('e-filing') }; })(),
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; creating.value = false; return; }
    }
    progress.value = 'Preparing documents…';
    const seen = new Set<number>();
    const docs = (props.selection.documents || []).filter((d: any) => { const tid = Number(d?.tmpId || d?.templateId || 0); if (!tid) return true; if (seen.has(tid)) return false; seen.add(tid); return true; });
    const strip = (s: string) => s.split(' — ')[0].trim();
    const docsToSave = docs.map((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return null;
      const rawName = String(d?.templateName || d?.docCode || 'AGM');
      const label = strip(rawName.replace(/\.docx?$/i, ''));
      const kind = classifyDoc(rawName);
      const signers = signersFor(kind);
      return { templateId: tid, label, personName: st(companyBase.chairman_name) || st(companyBase.company_name) || 'Company', formData: { ...companyBase, ...signBlockLayout(kind) }, signers: signers?.length ? signers : undefined };
    }).filter(Boolean) as Array<{ templateId: number; label: string; personName: string; formData: Record<string, any>; signers?: SignerEntry[] }>;
    if (docsToSave.length) {
      try { const r = await fetch('/api/aiDocuments/incorporation/prepare-documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }) }).then((x) => x.json()); if (!r?.success) progress.value = `⚠ Documents: ${r?.message || 'unknown'}`; } catch (e: any) { progress.value = `⚠ Document save failed: ${e?.message || e}`; }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }
    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) { progress.value = e?.message || 'Create failed'; } finally { creating.value = false; }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="sv-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Active-Exempt-unaudited AGM' }}</div>
        <div class="co-subtitle">Singapura — AGM Documents (Active-Exempt, unaudited)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>
    <div v-if="!studio" class="co-title-row"><v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" /></div>
    <div class="co-doc"><AgmPackageDoc /></div>
    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">{{ editMode ? 'Update task' : 'Create Task' }}</v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.sv-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.sv-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
